import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { useSiteContent } from "@/hooks/useSiteContent";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { PhoneInput } from "@/components/ui/phone-input";

const enrollmentSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  phone: z.string().trim().min(1, "Phone number is required").max(20),
  email: z.string().trim().email("Invalid email address").max(255),
  honeypot: z.string().optional(),
});

type EnrollmentFormData = z.infer<typeof enrollmentSchema>;

export const EnrollmentForm = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { content } = useSiteContent('bibleschool-enrollment');

  const form = useForm<EnrollmentFormData>({
    resolver: zodResolver(enrollmentSchema),
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      honeypot: "",
    },
  });

  const onSubmit = async (data: EnrollmentFormData) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("send-enrollment-email", {
        body: { 
          name: data.name,
          phone: data.phone,
          email: data.email,
          honeypot: data.honeypot,
        },
      });

      if (error) throw error;

      toast({
        title: "Enrollment inquiry sent!",
        description: "We'll contact you soon about Bible School enrollment.",
      });
      
      form.reset();
    } catch (error) {
      console.error("Error sending enrollment inquiry:", error);
      toast({
        title: "Error",
        description: "Failed to send enrollment inquiry. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {/* Honeypot field - hidden from users, catches bots */}
        <FormField
          control={form.control}
          name="honeypot"
          render={({ field }) => (
            <FormItem className="hidden">
              <FormLabel>Leave this field empty</FormLabel>
              <FormControl>
                <Input {...field} tabIndex={-1} autoComplete="off" />
              </FormControl>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
          <FormItem>
              <FormLabel>{content.label_name || 'Name'}</FormLabel>
              <FormControl>
                <Input placeholder={content.label_name || 'Your name'} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{content.label_phone || 'Phone Number'}</FormLabel>
                <FormControl>
                  <PhoneInput placeholder={content.label_phone || 'Your phone number'} value={field.value} onChange={field.onChange} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
          <FormItem>
              <FormLabel>{content.label_email || 'Email Address'}</FormLabel>
              <FormControl>
                <Input type="email" placeholder={content.label_email || 'your.email@example.com'} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" disabled={isSubmitting} className="w-full">
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {content.button_submit || 'Submit Enrollment Inquiry'}
        </Button>
      </form>
    </Form>
  );
};
